import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, ApiError, session } from "../api";
import { Banner } from "../components/ui";
import { AuthStage } from "../components/AuthStage";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState<string[]>(Array(6).fill(""));
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const notice = (useLocation().state as { notice?: string } | null)?.notice;
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (needsTotp) boxes.current[0]?.focus();
  }, [needsTotp]);

  const totp = code.join("");

  /** One box per digit, with paste spread across all six and backspace
   *  stepping back — the pattern every OS two-factor prompt already uses, so
   *  nobody has to work out how it behaves. */
  function setDigit(i: number, raw: string) {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      setCode((c) => c.map((v, j) => (j === i ? "" : v)));
      return;
    }
    setCode((c) => {
      const next = [...c];
      for (let k = 0; k < digits.length && i + k < 6; k++) next[i + k] = digits[k]!;
      return next;
    });
    boxes.current[Math.min(5, i + digits.length)]?.focus();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await api.login(email, password, needsTotp ? totp : undefined);
      session.set(res.token);
      // /me returns a role and an id but no name, and the sidebar should say
      // who is signed in. The email is the user's own, typed into their own
      // browser a line ago — keeping it beside the token is the honest way to
      // show it without inventing an identity from a uuid.
      try { sessionStorage.setItem("otc.who", email); } catch { /* non-fatal */ }
      if (res.must_change_password || res.must_setup_totp) {
        navigate("/enroll", {
          state: { mustChangePassword: res.must_change_password, mustSetupTotp: res.must_setup_totp },
        });
      } else {
        navigate("/");
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "TOTP_REQUIRED") {
        setNeedsTotp(true);
      } else if (err instanceof ApiError && err.code === "TOTP_INVALID") {
        setNeedsTotp(true);
        setCode(Array(6).fill(""));
        setError("That code was not accepted. Codes expire every 30 seconds.");
        boxes.current[0]?.focus();
      } else if (err instanceof ApiError && err.code === "ACCOUNT_LOCKED") {
        setError("Account locked after repeated failed attempts. Try again in 15 minutes.");
      } else {
        setError("Invalid email or password.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <AuthStage />
      <div className="auth-panel">
      <form className="auth-form" onSubmit={submit}>
        {/* The mark carries the name, so the heading says what this screen is
            for instead of repeating it. */}
        <h1>{needsTotp ? "Two-factor" : "Sign in"}</h1>
        <div className="sub">
          {needsTotp ? "Enter the 6-digit code from your authenticator." : "OTC desk operations"}
        </div>

        {notice && <div style={{ marginBottom: 16 }}><Banner tone="ok">{notice}</Banner></div>}

        {!needsTotp ? (
          <>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="username" value={email}
                     onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" autoComplete="current-password" value={password}
                     onChange={(e) => setPassword(e.target.value)} required />
            </div>
          </>
        ) : (
          <div className="otp" role="group" aria-label="Authenticator code">
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => { boxes.current[i] = el; }}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={6}
                value={d}
                aria-label={`Digit ${i + 1}`}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !code[i] && i > 0) boxes.current[i - 1]?.focus();
                }}
              />
            ))}
          </div>
        )}

        {error && <div style={{ marginTop: 16 }}><Banner tone="error">{error}</Banner></div>}

        <button className="btn btn-primary btn-lg btn-block" style={{ marginTop: 24 }}
                disabled={busy || (needsTotp && totp.length < 6)}>
          {busy ? "Signing in" : needsTotp ? "Verify code" : "Sign in"}
        </button>
      </form>
      </div>
    </div>
  );
}
